import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

interface VersionInfo {
  version: string;
  buildTime: string;
  buildId: string;
}

interface UseVersionCheckReturn {
  currentVersion: VersionInfo | null;
  checkForUpdates: () => Promise<void>;
}

const VERSION_CHECK_INTERVAL = 10 * 60 * 1000; // 10 minutos
const VERSION_STORAGE_KEY = 'app_version_info';

// Función para realizar actualización automática silenciosa
const performAutoUpdate = async (latestVersion: VersionInfo): Promise<void> => {
  try {
    logger.info('Starting silent update process');
    
    // Limpiar cache del navegador de forma asíncrona (no bloquea)
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        return Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }).then(() => {
        logger.info('Cache cleared for update');
      }).catch(error => {
        logger.error('Error clearing cache:', error);
      });
    }
    
    // Actualizar versión almacenada
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(latestVersion));
    logger.info('Version info updated in localStorage');
    
    // Recargar inmediatamente sin pausas
    logger.info('Reloading page for silent update');
    window.location.reload();
    
  } catch (error) {
    logger.error('Error during silent update:', error);
    // En caso de error, intentar recargar de todas formas
    window.location.reload();
  }
};

export const useVersionCheck = (): UseVersionCheckReturn => {
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);

  const fetchVersionInfo = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const response = await fetch('/version.json?' + Date.now(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const versionInfo = await response.json();
      logger.debug('Version info fetched:', versionInfo);
      return versionInfo;
    } catch (error) {
      logger.error('Error fetching version info:', error);
      return null;
    }
  }, []);

  const checkForUpdates = useCallback(async () => {
    try {
      const latestVersion = await fetchVersionInfo();
      
      if (!latestVersion) {
        return;
      }

      // Obtener versión almacenada localmente
      const storedVersionStr = localStorage.getItem(VERSION_STORAGE_KEY);
      const storedVersion = storedVersionStr ? JSON.parse(storedVersionStr) : null;

      // Si no hay versión almacenada, es la primera vez
      if (!storedVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(latestVersion));
        setCurrentVersion(latestVersion);
        logger.debug('First time loading, version stored:', latestVersion);
      } else {
        // Comparar versiones
        const isDifferent = 
          storedVersion.version !== latestVersion.version ||
          storedVersion.buildId !== latestVersion.buildId ||
          storedVersion.buildTime !== latestVersion.buildTime;

        if (isDifferent) {
          logger.info('New version detected, updating silently:', {
            current: storedVersion,
            latest: latestVersion
          });
          
          // Actualizar automáticamente sin indicadores visuales
          await performAutoUpdate(latestVersion);
        }
        
        setCurrentVersion(storedVersion);
      }
    } catch (error) {
      logger.error('Error checking for updates:', error);
    }
  }, [fetchVersionInfo]);

  // Verificar actualizaciones al montar el componente
  useEffect(() => {
    checkForUpdates();
  }, [checkForUpdates]);

  // Verificar actualizaciones periódicamente
  useEffect(() => {
    const interval = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [checkForUpdates]);

  // Escuchar eventos de visibilidad para verificar cuando el usuario vuelve a la app
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkForUpdates]);

  return {
    currentVersion,
    checkForUpdates
  };
};
